import React from 'react';
import { Layout, Menu, Avatar, Button, Typography } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  PictureOutlined,
  LogoutOutlined,
  UserOutlined,
  TagOutlined,
  TeamOutlined,
  MenuOutlined,
  FormOutlined,
  LayoutOutlined,
  PhoneOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCmsAuth } from '../contexts/CmsAuthContext';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

interface CmsLayoutProps {
  children: React.ReactNode;
}

const CmsLayout: React.FC<CmsLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useCmsAuth();

  const menuItems = [
    {
      key: '/cms/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/cms/team-members',
      icon: <TeamOutlined />,
      label: 'Team Members',
    },
    {
      key: '/cms/case-studies',
      icon: <FileTextOutlined />,
      label: 'Case Studies',
    },
    {
      key: '/cms/posts',
      icon: <FileTextOutlined />,
      label: 'Blog Posts',
    },
    {
      key: '/cms/media',
      icon: <PictureOutlined />,
      label: 'Media Library',
    },
    {
      key: '/cms/faqs',
      icon: <FileTextOutlined />,
      label: 'FAQ Management',
    },
    {
      key: '/cms/heroes',
      icon: <PictureOutlined />,
      label: 'Hero Sections',
    },
    {
      key: '/cms/seo',
      icon: <GlobalOutlined />,
      label: 'SEO Management',
    },
    {
      key: '/cms/homepage-sections',
      icon: <LayoutOutlined />,
      label: 'Homepage Sections',
    },
    {
      key: '/cms/landing-content',
      icon: <LayoutOutlined />,
      label: 'Landing Content',
    },
    {
      key: '/cms/service-sections',
      icon: <LayoutOutlined />,
      label: 'Service Sections',
    },
    {
      key: '/cms/product-sections',
      icon: <LayoutOutlined />,
      label: 'Product Sections',
    },
    {
      key: '/cms/cta-sections',
      icon: <LayoutOutlined />,
      label: 'CTA Sections',
    },
    {
      key: '/cms/contact-details',
      icon: <PhoneOutlined />,
      label: 'Contact Details',
    },
    {
      key: '/cms/navigation',
      icon: <MenuOutlined />,
      label: 'Navigation & Footer',
    },
    {
      key: '/cms/contact-form',
      icon: <FormOutlined />,
      label: 'Contact Form',
    },
    {
      key: '/cms/tags',
      icon: <TagOutlined />,
      label: 'Tag Management',
    },
  ];

  // All menu items are visible to authenticated users now
  const visibleMenuItems = menuItems;

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/cms/login');
  };

  return (
    <Layout className="min-h-screen bg-gray-50">
      <Sider
        width={260}
        className="bg-white border-r border-gray-200 shadow-sm"
        theme="light"
        style={{
          position: 'fixed',
          height: '100vh',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 50
        }}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
              C
            </div>
            <div>
              <Title level={5} className="mb-0 text-gray-800 font-bold tracking-tight">
                CMS Admin
              </Title>
              <p className="text-xs text-gray-500 font-medium">Content Management</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-4 scrollbar-thin">
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={visibleMenuItems}
              onClick={handleMenuClick}
              className="border-none px-2"
              style={{ border: 'none' }}
            />
          </div>

          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white hover:shadow-sm transition-all duration-200 cursor-pointer" onClick={() => { }}>
              <Avatar size="small" icon={<UserOutlined />} className="bg-primary-100 text-primary-600" />
              <div className="overflow-hidden">
                <p className="text-sm font-medium text-gray-700 truncate">{user?.displayName || user?.email?.split('@')[0] || 'Admin User'}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email || 'admin@brai.com'}</p>
              </div>
            </div>
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 260 }}>
        <Header
          className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40 px-6 flex justify-between items-center h-16 shadow-none"
          style={{ padding: '0 24px', background: 'rgba(255, 255, 255, 0.8)' }}
        >
          <div>
            <Title level={4} className="m-0 text-gray-800 font-semibold tracking-tight">
              {visibleMenuItems.find(item => item.key === location.pathname)?.label || 'CMS'}
            </Title>
          </div>

          <div className="flex items-center gap-4">
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="text-gray-500 hover:text-red-600 hover:bg-red-50"
            >
              Logout
            </Button>
          </div>
        </Header>

        <Content className="p-6 md:p-8 bg-gray-50/50 min-h-[calc(100vh-64px)]">
          <div className="max-w-7xl mx-auto animate-fade-in">
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default CmsLayout;